import React, { useState } from 'react';
import { Button } from '../../components/ui/Button';
import { Tabs } from '../../components/ui/Tabs';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { ArrowLeft, Check, X, AlertCircle, FileText, Send, CheckCircle, XCircle, ShieldAlert, Eye, Search, Banknote, Paperclip, Upload } from 'lucide-react';
import { mockApplications } from './MasterView';
interface ApplicationDetailProps {
  applicationId: string;
  onBack: () => void;
  userRole: 'maker' | 'checker';
  userEmail: string;
  onStatusChange: (appId: string, newStatus: string, comment: string, actionBy: string) => void;
}
export function ApplicationDetail({
  applicationId,
  onBack,
  userRole,
  userEmail,
  onStatusChange
}: ApplicationDetailProps) {
  const [activeTab, setActiveTab] = useState('overview');
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionType, setActionType] = useState<'submit' | 'approve' | 'reject' | 'disburse'>('submit');
  const [comment, setComment] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const app = mockApplications.find(a => a.id === applicationId) || mockApplications[0];
  const tabs = [{
    id: 'overview',
    label: 'Overview'
  }, {
    id: 'documents',
    label: 'Documents'
  }, {
    id: 'risk',
    label: 'Risk Rating'
  }, {
    id: 'tasdeek',
    label: 'Tasdeeq'
  }, {
    id: 'calculated',
    label: 'Calculated Values'
  }, {
    id: 'kyc',
    label: 'KYC & Credit'
  }, {
    id: 'rules',
    label: 'Rule Engine'
  }, {
    id: 'logs',
    label: 'Activity Log'
  }];
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-PK', {
      style: 'currency',
      currency: 'PKR',
      maximumFractionDigits: 0
    }).format(amount);
  };
  const handleAction = (type: 'submit' | 'approve' | 'reject' | 'disburse') => {
    setActionType(type);
    setComment('');
    setAttachments([]);
    setShowActionModal(true);
  };
  const handleAttachmentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setAttachments(prev => [...prev, ...Array.from(e.target.files!)]);
    }
    e.target.value = '';
  };
  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };
  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };
  const handleConfirmAction = () => {
    setIsProcessing(true);
    setTimeout(() => {
      let newStatus = app.status;
      if (actionType === 'submit') {
        newStatus = 'Submitted to Checker';
      } else if (actionType === 'approve') {
        newStatus = 'Accepted';
      } else if (actionType === 'reject') {
        newStatus = 'Rejected';
      } else if (actionType === 'disburse') {
        newStatus = 'Disbursed';
      }
      onStatusChange(applicationId, newStatus, comment, userEmail);
      setIsProcessing(false);
      setShowActionModal(false);
      onBack();
    }, 1500);
  };
  const canMakerAct = userRole === 'maker' && app.status === 'Pending Approval';
  const canCheckerAct = userRole === 'checker' && app.status === 'Submitted to Checker';
  const canDisburse = app.status === 'Disbursement Ready';
  const renderOverview = () => <div className="space-y-8 animate-in fade-in duration-300" data-id="element-1256">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1257">
        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm" data-id="element-1258">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2" data-id="element-1259">
            Application Data
          </h3>
          <dl className="grid grid-cols-2 gap-4" data-id="element-1260">
            <dt className="text-sm font-medium text-gray-500" data-id="element-1261">
              Application ID
            </dt>
            <dd className="text-sm text-gray-900 font-mono" data-id="element-1262">{app.id}</dd>
            <dt className="text-sm font-medium text-gray-500" data-id="element-1263">
              Application Date
            </dt>
            <dd className="text-sm text-gray-900" data-id="element-1264">{app.date}</dd>
            <dt className="text-sm font-medium text-gray-500" data-id="element-1265">Product Code</dt>
            <dd className="text-sm text-gray-900" data-id="element-1266">{app.productId}</dd>
            <dt className="text-sm font-medium text-gray-500" data-id="element-1267">Loan Amount</dt>
            <dd className="text-sm font-semibold text-blue-600" data-id="element-1268">
              {formatCurrency(app.loanAmount)}
            </dd>
            <dt className="text-sm font-medium text-gray-500" data-id="element-1269">Tenor</dt>
            <dd className="text-sm text-gray-900" data-id="element-1270">{app.loanTenor} Months</dd>
          </dl>
        </div>

        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm" data-id="element-1271">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2" data-id="element-1272">
            Merchant Details
          </h3>
          <dl className="grid grid-cols-2 gap-4" data-id="element-1273">
            <dt className="text-sm font-medium text-gray-500" data-id="element-1274">Merchant ID</dt>
            <dd className="text-sm text-gray-900 font-mono" data-id="element-1275">
              {app.merchantId}
            </dd>
            <dt className="text-sm font-medium text-gray-500" data-id="element-1276">Merchant Name</dt>
            <dd className="text-sm text-gray-900" data-id="element-1277">{app.merchantName}</dd>
            <dt className="text-sm font-medium text-gray-500" data-id="element-1278">Type</dt>
            <dd className="text-sm text-gray-900" data-id="element-1279">{app.merchantType}</dd>
            <dt className="text-sm font-medium text-gray-500" data-id="element-1280">Partner</dt>
            <dd className="text-sm text-gray-900" data-id="element-1281">{app.partnerType}</dd>
            <dt className="text-sm font-medium text-gray-500" data-id="element-1282">Contact</dt>
            <dd className="text-sm text-gray-900" data-id="element-1283">
              contact@{app.merchantName.toLowerCase().replace(/\s/g, '')}.com
            </dd>
            <dt className="text-sm font-medium text-gray-500" data-id="element-1284">
              KYC Validity Status
            </dt>
            <dd className="text-sm" data-id="element-1285">
              {(() => {
              // Mock KYC age in days (you can replace this with actual data)
              const kycAgeDays = Math.floor(Math.random() * 200);
              let status = 'Fresh';
              let statusColor = 'bg-green-100 text-green-800';
              if (kycAgeDays > 180) {
                status = 'Expired';
                statusColor = 'bg-red-100 text-red-800';
              } else if (kycAgeDays > 90) {
                status = 'Stale';
                statusColor = 'bg-yellow-100 text-yellow-800';
              }
              return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColor}`} data-id="element-1286">
                    {status}
                  </span>;
            })()}
            </dd>
          </dl>
        </div>

        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm" data-id="element-1287">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2" data-id="element-1288">
            Disbursement Details
          </h3>
          <dl className="grid grid-cols-2 gap-4" data-id="element-1289">
            <dt className="text-sm font-medium text-gray-500" data-id="element-1290">Method</dt>
            <dd className="text-sm text-gray-900" data-id="element-1291">Bank Transfer</dd>
            <dt className="text-sm font-medium text-gray-500" data-id="element-1292">Bank Name</dt>
            <dd className="text-sm text-gray-900" data-id="element-1293">Chase Bank</dd>
            <dt className="text-sm font-medium text-gray-500" data-id="element-1294">
              Account Number
            </dt>
            <dd className="text-sm text-gray-900 font-mono" data-id="element-1295">**** 4589</dd>
            <dt className="text-sm font-medium text-gray-500" data-id="element-1296">Routing</dt>
            <dd className="text-sm text-gray-900 font-mono" data-id="element-1297">021000021</dd>
          </dl>
        </div>

        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm" data-id="element-1298">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2" data-id="element-1299">
            Repayment Details
          </h3>
          <dl className="grid grid-cols-2 gap-4" data-id="element-1300">
            <dt className="text-sm font-medium text-gray-500" data-id="element-1301">Method</dt>
            <dd className="text-sm text-gray-900" data-id="element-1302">Auto-Debit</dd>
            <dt className="text-sm font-medium text-gray-500" data-id="element-1303">Channel</dt>
            <dd className="text-sm text-gray-900" data-id="element-1304">ACH</dd>
            <dt className="text-sm font-medium text-gray-500" data-id="element-1305">First Payment</dt>
            <dd className="text-sm text-gray-900" data-id="element-1306">2024-02-01</dd>
            <dt className="text-sm font-medium text-gray-500" data-id="element-1307">Frequency</dt>
            <dd className="text-sm text-gray-900" data-id="element-1308">Monthly</dd>
          </dl>
        </div>
      </div>
    </div>;
  const renderCalculated = () => <div className="space-y-8 animate-in fade-in duration-300" data-id="element-1309">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1310">
        <div className="bg-blue-50 p-6 rounded-lg border border-blue-100" data-id="element-1311">
          <p className="text-sm font-medium text-blue-600 mb-1" data-id="element-1312">
            Principal Amount
          </p>
          <p className="text-3xl font-bold text-blue-900" data-id="element-1313">
            {formatCurrency(app.principalAmount)}
          </p>
        </div>
        <div className="bg-green-50 p-6 rounded-lg border border-green-100" data-id="element-1314">
          <p className="text-sm font-medium text-green-600 mb-1" data-id="element-1315">
            Total Repayment
          </p>
          <p className="text-3xl font-bold text-green-900" data-id="element-1316">
            {formatCurrency(app.totalRepayment)}
          </p>
        </div>
        <div className="bg-purple-50 p-6 rounded-lg border border-purple-100" data-id="element-1317">
          <p className="text-sm font-medium text-purple-600 mb-1" data-id="element-1318">APR</p>
          <p className="text-3xl font-bold text-purple-900" data-id="element-1319">{app.apr}%</p>
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm" data-id="element-1320">
        <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1321">
          Fee Breakdown
        </h3>
        <div className="space-y-3" data-id="element-1322">
          <div className="flex justify-between text-sm" data-id="element-1323">
            <span className="text-gray-500" data-id="element-1324">Processing Fee</span>
            <span className="text-gray-900 font-medium" data-id="element-1325">
              {formatCurrency(app.fees * 0.6)}
            </span>
          </div>
          <div className="flex justify-between text-sm" data-id="element-1326">
            <span className="text-gray-500" data-id="element-1327">Origination Fee</span>
            <span className="text-gray-900 font-medium" data-id="element-1328">
              {formatCurrency(app.fees * 0.3)}
            </span>
          </div>
          <div className="flex justify-between text-sm" data-id="element-1329">
            <span className="text-gray-500" data-id="element-1330">Tax</span>
            <span className="text-gray-900 font-medium" data-id="element-1331">
              {formatCurrency(app.fees * 0.1)}
            </span>
          </div>
          <div className="border-t pt-3 flex justify-between text-base font-semibold" data-id="element-1332">
            <span className="text-gray-900" data-id="element-1333">Total Fees</span>
            <span className="text-gray-900" data-id="element-1334">{formatCurrency(app.fees)}</span>
          </div>
        </div>
      </div>
    </div>;
  const renderKYC = () => <div className="space-y-8 animate-in fade-in duration-300" data-id="element-1335">
      <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm" data-id="element-1336">
        <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1337">
          KYC Verification
        </h3>
        <div className="flex items-center space-x-4 mb-6" data-id="element-1338">
          <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center" data-id="element-1339">
            <Check className="h-6 w-6 text-green-600" data-id="element-1340" />
          </div>
          <div data-id="element-1341">
            <p className="text-lg font-medium text-gray-900" data-id="element-1342">KYC Verified</p>
            <p className="text-sm text-gray-500" data-id="element-1343">
              All required documents have been approved.
            </p>
          </div>
        </div>
        <div className="space-y-3" data-id="element-1344">
          {['National ID Card', 'Proof of Address', 'Bank Statement (3 months)'].map(doc => <div key={doc} className="flex items-center justify-between p-3 bg-gray-50 rounded-md border border-gray-100" data-id="element-1345">
              <div className="flex items-center" data-id="element-1346">
                <FileText className="h-5 w-5 text-gray-400 mr-3" data-id="element-1347" />
                <span className="text-sm font-medium text-gray-700" data-id="element-1348">{doc}</span>
              </div>
              <div className="flex items-center space-x-3" data-id="element-1349">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-1350">
                  Verified
                </span>
                <Button size="sm" variant="ghost" data-id="element-1351">
                  View
                </Button>
              </div>
            </div>)}
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm" data-id="element-1352">
        <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1353">
          Credit Score
        </h3>
        <div className="flex items-center justify-between mb-6" data-id="element-1354">
          <div data-id="element-1355">
            <p className="text-4xl font-bold text-blue-600" data-id="element-1356">785</p>
            <p className="text-sm text-gray-500 mt-1" data-id="element-1357">Excellent Score</p>
          </div>
          <div className="text-right" data-id="element-1358">
            <p className="text-sm font-medium text-gray-900" data-id="element-1359">Model: FICO v9</p>
            <p className="text-sm text-gray-500" data-id="element-1360">Date: {app.date}</p>
          </div>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2.5 mb-2" data-id="element-1361">
          <div className="bg-blue-600 h-2.5 rounded-full" style={{
          width: '85%'
        }} data-id="element-1362"></div>
        </div>
        <div className="flex justify-between text-xs text-gray-500" data-id="element-1363">
          <span data-id="element-1364">300</span>
          <span data-id="element-1365">850</span>
        </div>
      </div>
    </div>;
  const renderRules = () => <div className="space-y-6 animate-in fade-in duration-300" data-id="element-1366">
      <div className="flex items-center space-x-4 bg-green-50 p-6 rounded-lg border border-green-100" data-id="element-1367">
        <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0" data-id="element-1368">
          <Check className="h-8 w-8 text-green-600" data-id="element-1369" />
        </div>
        <div data-id="element-1370">
          <h3 className="text-xl font-bold text-green-900" data-id="element-1371">
            Rule Engine Passed
          </h3>
          <p className="text-green-700" data-id="element-1372">
            Application meets all automated criteria for approval.
          </p>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" data-id="element-1373">
        <table className="min-w-full divide-y divide-gray-200" data-id="element-1374">
          <thead className="bg-gray-50" data-id="element-1375">
            <tr data-id="element-1376">
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1377">
                Rule Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1378">
                Condition
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1379">
                Result
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1380">
                Weight
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200" data-id="element-1381">
            <tr data-id="element-1382">
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-1383">
                Minimum Credit Score
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1384">
                Score &gt; 650
              </td>
              <td className="px-6 py-4 whitespace-nowrap" data-id="element-1385">
                <span className="text-green-600 font-medium flex items-center" data-id="element-1386">
                  <Check className="h-4 w-4 mr-1" data-id="element-1387" /> Pass
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1388">
                High
              </td>
            </tr>
            <tr data-id="element-1389">
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-1390">
                Debt-to-Income Ratio
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1391">
                DTI &lt; 40%
              </td>
              <td className="px-6 py-4 whitespace-nowrap" data-id="element-1392">
                <span className="text-green-600 font-medium flex items-center" data-id="element-1393">
                  <Check className="h-4 w-4 mr-1" data-id="element-1394" /> Pass
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1395">
                High
              </td>
            </tr>
            <tr data-id="element-1396">
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-1397">
                Age Eligibility
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1398">
                Age &gt; 21
              </td>
              <td className="px-6 py-4 whitespace-nowrap" data-id="element-1399">
                <span className="text-green-600 font-medium flex items-center" data-id="element-1400">
                  <Check className="h-4 w-4 mr-1" data-id="element-1401" /> Pass
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1402">
                Medium
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>;
  const renderDocuments = () => {
    const submittedDocs = [{
      name: 'CNIC Front Copy',
      date: '2024-01-12',
      status: 'Submitted',
      type: 'Identity'
    }, {
      name: 'CNIC Back Copy',
      date: '2024-01-12',
      status: 'Submitted',
      type: 'Identity'
    }, {
      name: 'Utility Bill',
      date: '2024-01-13',
      status: 'Submitted',
      type: 'Address Proof'
    }, {
      name: 'Bank Statement (3 months)',
      date: '2024-01-13',
      status: 'Submitted',
      type: 'Financial'
    }, {
      name: 'Business Registration Certificate',
      date: '2024-01-14',
      status: 'Submitted',
      type: 'Business'
    }, {
      name: 'Tax Return (Last Year)',
      date: '2024-01-14',
      status: 'Pending',
      type: 'Financial'
    }, {
      name: 'Shop Premises Photo',
      date: '2024-01-14',
      status: 'Submitted',
      type: 'Business'
    }];
    const statusColors: Record<string, string> = {
      Submitted: 'bg-blue-100 text-blue-800',
      Pending: 'bg-yellow-100 text-yellow-800',
      Rejected: 'bg-red-100 text-red-800'
    };
    const typeColors: Record<string, string> = {
      Identity: 'bg-purple-50 text-purple-700',
      'Address Proof': 'bg-teal-50 text-teal-700',
      Financial: 'bg-green-50 text-green-700',
      Business: 'bg-orange-50 text-orange-700'
    };
    return <div className="space-y-6 animate-in fade-in duration-300" data-id="element-1403">
        <div className="flex items-center justify-between" data-id="element-1404">
          <div data-id="element-1405">
            <h3 className="text-lg font-semibold text-gray-900" data-id="element-1406">
              Submitted Documents
            </h3>
            <p className="text-sm text-gray-500 mt-1" data-id="element-1407">
              Raw documents submitted by the applicant during the loan
              application process.
            </p>
          </div>
          <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full" data-id="element-1408">
            {submittedDocs.length} documents
          </span>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" data-id="element-1409">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-1410">
            <thead className="bg-gray-50" data-id="element-1411">
              <tr data-id="element-1412">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1413">
                  Document Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1414">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1415">
                  Submission Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1416">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1417">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-1418">
              {submittedDocs.map(doc => <tr key={doc.name} className="hover:bg-gray-50 transition-colors" data-id="element-1419">
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-1420">
                    <div className="flex items-center" data-id="element-1421">
                      <FileText className="h-4 w-4 text-gray-400 mr-3 flex-shrink-0" data-id="element-1422" />
                      <span className="text-sm font-medium text-gray-900" data-id="element-1423">
                        {doc.name}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-1424">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${typeColors[doc.type] || 'bg-gray-100 text-gray-700'}`} data-id="element-1425">
                      {doc.type}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1426">
                    {doc.date}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-1427">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[doc.status] || 'bg-gray-100 text-gray-800'}`} data-id="element-1428">
                      {doc.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-1429">
                    <Button size="sm" variant="ghost" className="text-blue-600 hover:text-blue-700 hover:bg-blue-50" data-id="element-1430">
                      <Eye className="h-3.5 w-3.5 mr-1.5" data-id="element-1431" />
                      View
                    </Button>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start space-x-3" data-id="element-1432">
          <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-1433" />
          <p className="text-sm text-amber-800" data-id="element-1434">
            These are raw submitted documents only. For verified KYC documents,
            refer to the <strong data-id="element-1435">KYC & Credit</strong> tab.
          </p>
        </div>
      </div>;
  };
  const renderRiskRating = () => {
    const riskScore = 53.1;
    const riskCategory = riskScore <= 30 ? 'Low' : riskScore <= 60 ? 'Medium' : 'High';
    const riskColor = riskCategory === 'Low' ? {
      bg: 'bg-green-50',
      border: 'border-green-200',
      text: 'text-green-800',
      badge: 'bg-green-100 text-green-800',
      bar: 'bg-green-500'
    } : riskCategory === 'Medium' ? {
      bg: 'bg-yellow-50',
      border: 'border-yellow-200',
      text: 'text-yellow-800',
      badge: 'bg-yellow-100 text-yellow-800',
      bar: 'bg-yellow-500'
    } : {
      bg: 'bg-red-50',
      border: 'border-red-200',
      text: 'text-red-800',
      badge: 'bg-red-100 text-red-800',
      bar: 'bg-red-500'
    };
    const nactaScores = {
      rows: [{
        metric: 'Father Name Score',
        weightage: '20%',
        score: '84.0'
      }, {
        metric: 'CNIC Score',
        weightage: '40%',
        score: '62.0'
      }, {
        metric: 'Name Score',
        weightage: '30%',
        score: '58.0'
      }],
      total: '53.1'
    };
    const unscScores = {
      rows: [{
        metric: 'CNIC Score',
        weightage: '40%',
        score: '47.0'
      }, {
        metric: 'Name Score',
        weightage: '30%',
        score: '40.0'
      }, {
        metric: 'DOB Score',
        weightage: '10%',
        score: '60.0'
      }],
      total: '29.44'
    };
    const renderScoreTable = (title: string, data: {
      rows: {
        metric: string;
        weightage: string;
        score: string;
      }[];
      total: string;
    }) => <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" data-id="element-1436">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center space-x-2" data-id="element-1437">
          <Search className="h-4 w-4 text-blue-500" data-id="element-1438" />
          <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider" data-id="element-1439">
            {title}
          </h3>
        </div>
        <table className="min-w-full divide-y divide-gray-200" data-id="element-1440">
          <thead className="bg-gray-50" data-id="element-1441">
            <tr data-id="element-1442">
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1443">
                Metric
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1444">
                Weightage
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1445">
                Score
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200" data-id="element-1446">
            {data.rows.map(row => <tr key={row.metric} className="hover:bg-gray-50 transition-colors" data-id="element-1447">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-1448">
                  {row.metric}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-600 tabular-nums" data-id="element-1449">
                  {row.weightage}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900 tabular-nums" data-id="element-1450">
                  {row.score}
                </td>
              </tr>)}
            <tr className="bg-gray-50" data-id="element-1451">
              <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900" data-id="element-1452">
                Total Score
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-400 tabular-nums" data-id="element-1453">
                –
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-semibold text-gray-900 tabular-nums" data-id="element-1454">
                {data.total}
              </td>
            </tr>
          </tbody>
        </table>
      </div>;
    return <div className="space-y-6 animate-in fade-in duration-300" data-id="element-1455">
        {/* Risk Summary Card */}
        <div className={`${riskColor.bg} border ${riskColor.border} rounded-lg p-6`} data-id="element-1456">
          <div className="flex items-center justify-between mb-4" data-id="element-1457">
            <div className="flex items-center space-x-4" data-id="element-1458">
              <div className={`h-16 w-16 rounded-full ${riskColor.badge} flex items-center justify-center flex-shrink-0`} data-id="element-1459">
                <ShieldAlert className={`h-8 w-8 ${riskColor.text}`} data-id="element-1460" />
              </div>
              <div data-id="element-1461">
                <p className="text-sm font-medium text-gray-500" data-id="element-1462">
                  Overall Risk Category
                </p>
                <p className={`text-3xl font-bold ${riskColor.text}`} data-id="element-1463">
                  {riskCategory} Risk
                </p>
              </div>
            </div>
            <div className="text-right" data-id="element-1464">
              <p className="text-sm font-medium text-gray-500" data-id="element-1465">Risk Score</p>
              <p className={`text-4xl font-bold ${riskColor.text}`} data-id="element-1466">
                {riskScore.toFixed(1)}
              </p>
              <p className="text-xs text-gray-500 mt-1" data-id="element-1467">out of 100</p>
            </div>
          </div>

          {/* Score Bar */}
          <div className="mt-2" data-id="element-1468">
            <div className="flex justify-between text-xs text-gray-500 mb-1" data-id="element-1469">
              <span data-id="element-1470">Low (0–30)</span>
              <span data-id="element-1471">Medium (31–60)</span>
              <span data-id="element-1472">High (61–100)</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3 relative overflow-hidden" data-id="element-1473">
              <div className="absolute inset-0 flex" data-id="element-1474">
                <div className="h-full bg-green-200" style={{
                width: '30%'
              }} data-id="element-1475" />
                <div className="h-full bg-yellow-200" style={{
                width: '30%'
              }} data-id="element-1476" />
                <div className="h-full bg-red-200" style={{
                width: '40%'
              }} data-id="element-1477" />
              </div>
              <div className={`absolute top-0 left-0 h-full ${riskColor.bar} rounded-full transition-all`} style={{
              width: `${riskScore}%`
            }} data-id="element-1478" />
            </div>
          </div>

          {/* Age Scoring Factor */}
          <div className="mt-5 pt-4 border-t border-gray-200/70" data-id="element-1479">
            <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2" data-id="element-1480">
              Age Scoring Factor
            </p>
            <div className="flex flex-wrap gap-2" data-id="element-1481">
              {[{
              band: 'Under 18',
              delta: -10
            }, {
              band: '18 – 25',
              delta: -5
            }, {
              band: '26 – 45',
              delta: 10
            }, {
              band: '46 – 65',
              delta: 5
            }, {
              band: '65+',
              delta: 0
            }].map(item => <span key={item.band} className="inline-flex items-center gap-1.5 rounded-full bg-white/70 border border-gray-200 px-3 py-1 text-xs" data-id="element-1482">
                  <span className="font-medium text-gray-700" data-id="element-1483">{item.band}</span>
                  <span className={`font-semibold ${item.delta > 0 ? 'text-green-600' : item.delta < 0 ? 'text-red-600' : 'text-gray-500'}`} data-id="element-1484">
                    {item.delta === 0 ? 'No change' : `${item.delta > 0 ? '+' : ''}${item.delta}`}
                  </span>
                </span>)}
            </div>
          </div>
        </div>

        {/* Score Breakdown Tables */}
        {renderScoreTable('NACTA Scores', nactaScores)}
        {renderScoreTable('UNSC Scores', unscScores)}

        <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 flex items-start space-x-3" data-id="element-1485">
          <AlertCircle className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" data-id="element-1486" />
          <p className="text-sm text-blue-700" data-id="element-1487">
            This is a read-only risk assessment. Risk scores are calculated
            automatically and do not affect rule engine outcomes.
          </p>
        </div>
      </div>;
  };
  const renderLogs = () => <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden animate-in fade-in duration-300" data-id="element-1488">
      <table className="min-w-full divide-y divide-gray-200" data-id="element-1489">
        <thead className="bg-gray-50" data-id="element-1490">
          <tr data-id="element-1491">
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1492">
              Date/Time
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1493">
              Action
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1494">
              Action By
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1495">
              Comments
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200" data-id="element-1496">
          <tr data-id="element-1497">
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1498">
              2024-01-15 14:30
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-1499">
              Submitted to Checker
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1500">
              maker@bunyaadfinance.com
            </td>
            <td className="px-6 py-4 text-sm text-gray-500" data-id="element-1501">
              All documents verified and validated. Ready for checker review.
            </td>
          </tr>
          <tr data-id="element-1502">
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1503">
              2024-01-15 10:15
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-1504">
              KYC Verified
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1505">
              system
            </td>
            <td className="px-6 py-4 text-sm text-gray-500" data-id="element-1506">
              Automated eKYC check passed.
            </td>
          </tr>
          <tr data-id="element-1507">
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1508">
              2024-01-14 09:00
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-1509">
              Application Created
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1510">
              portal_api
            </td>
            <td className="px-6 py-4 text-sm text-gray-500" data-id="element-1511">
              Submitted via Merchant Portal.
            </td>
          </tr>
        </tbody>
      </table>
    </div>;
  const getActionModalContent = () => {
    switch (actionType) {
      case 'submit':
        return {
          title: 'Submit to Checker',
          description: 'Submit this application to the checker for final approval.',
          icon: <Send className="h-6 w-6 text-blue-600" data-id="element-1512" />,
          iconBg: 'bg-blue-100',
          buttonText: 'Submit to Checker',
          buttonVariant: 'primary' as const
        };
      case 'approve':
        return {
          title: 'Approve Application',
          description: 'Approve this application for disbursement.',
          icon: <CheckCircle className="h-6 w-6 text-green-600" data-id="element-1513" />,
          iconBg: 'bg-green-100',
          buttonText: 'Approve Application',
          buttonVariant: 'primary' as const
        };
      case 'reject':
        return {
          title: 'Reject Application',
          description: 'Reject this application. Please provide a reason.',
          icon: <XCircle className="h-6 w-6 text-red-600" data-id="element-1514" />,
          iconBg: 'bg-red-100',
          buttonText: 'Reject Application',
          buttonVariant: 'secondary' as const
        };
      case 'disburse':
        return {
          title: 'Mark as Disbursed',
          description: 'Confirm that the loan amount has been disbursed to the merchant. This will move the application to a Disbursed state.',
          icon: <Banknote className="h-6 w-6 text-emerald-600" data-id="element-1515" />,
          iconBg: 'bg-emerald-100',
          buttonText: 'Mark as Disbursed',
          buttonVariant: 'primary' as const
        };
    }
  };
  const modalContent = getActionModalContent();
  return <div className="space-y-6" data-id="element-1516">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-1517">
        <div className="flex items-center space-x-4" data-id="element-1518">
          <button onClick={onBack} className="p-2 rounded-full hover:bg-gray-100 text-gray-500 transition-colors" data-id="element-1519">
            <ArrowLeft className="h-5 w-5" data-id="element-1520" />
          </button>
          <div data-id="element-1521">
            <div className="flex items-center space-x-3" data-id="element-1522">
              <h1 className="text-2xl font-bold text-gray-900" data-id="element-1523">{app.id}</h1>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${app.status === 'Submitted to Checker' ? 'bg-purple-100 text-purple-800' : app.status === 'Active' ? 'bg-blue-100 text-blue-800' : app.status === 'Accepted' ? 'bg-green-100 text-green-800' : app.status === 'Disbursement Ready' ? 'bg-indigo-100 text-indigo-800' : app.status === 'Disbursed' ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`} data-id="element-1524">
                {app.status}
              </span>
            </div>
            <p className="text-sm text-gray-500 mt-1" data-id="element-1525">
              {app.merchantName} • {formatCurrency(app.loanAmount)}
            </p>
          </div>
        </div>
        <div className="flex space-x-3" data-id="element-1526">
          {canMakerAct && <>
              <Button variant="secondary" onClick={() => handleAction('reject')} data-id="element-1527">
                <X className="h-4 w-4 mr-2" data-id="element-1528" />
                Reject
              </Button>
              <Button onClick={() => handleAction('submit')} data-id="element-1529">
                <Send className="h-4 w-4 mr-2" data-id="element-1530" />
                Submit to Checker
              </Button>
            </>}
          {canCheckerAct && <>
              <Button variant="secondary" onClick={() => handleAction('reject')} data-id="element-1531">
                <X className="h-4 w-4 mr-2" data-id="element-1532" />
                Reject
              </Button>
              <Button onClick={() => handleAction('approve')} data-id="element-1533">
                <Check className="h-4 w-4 mr-2" data-id="element-1534" />
                Approve Application
              </Button>
            </>}
          {canDisburse && <Button onClick={() => handleAction('disburse')} data-id="element-1535">
              <Banknote className="h-4 w-4 mr-2" data-id="element-1536" />
              Mark as Disbursed
            </Button>}
          {!canMakerAct && !canCheckerAct && !canDisburse && <div className="text-sm text-gray-500 bg-gray-50 px-4 py-2 rounded-lg" data-id="element-1537">
              No actions available for current status
            </div>}
        </div>
      </div>

      {/* User Role Indicator */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-1538">
        <div className="flex items-center justify-between" data-id="element-1539">
          <div className="flex items-center space-x-3" data-id="element-1540">
            <AlertCircle className="h-5 w-5 text-blue-600" data-id="element-1541" />
            <div data-id="element-1542">
              <p className="text-sm font-medium text-blue-900" data-id="element-1543">
                Logged in as:{' '}
                {userRole.charAt(0).toUpperCase() + userRole.slice(1)}
              </p>
              <p className="text-xs text-blue-700" data-id="element-1544">{userEmail}</p>
            </div>
          </div>
          {canMakerAct && <span className="text-xs text-blue-700" data-id="element-1545">
              You can submit or reject this application
            </span>}
          {canCheckerAct && <span className="text-xs text-blue-700" data-id="element-1546">
              You can approve or reject this application
            </span>}
        </div>
      </div>

      {/* Tabs */}
      <Tabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} data-id="element-1547" />

      {/* Content */}
      <div className="min-h-[500px]" data-id="element-1548">
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'documents' && renderDocuments()}
        {activeTab === 'risk' && renderRiskRating()}
        {activeTab === 'tasdeek' && <div className="flex flex-col items-center justify-center min-h-[400px] text-center animate-in fade-in duration-300" data-id="element-1549">
            <div className="h-16 w-16 rounded-full bg-gray-100 flex items-center justify-center mb-4" data-id="element-1550">
              <FileText className="h-8 w-8 text-gray-400" data-id="element-1551" />
            </div>
            <h3 className="text-lg font-semibold text-gray-700 mb-2" data-id="element-1552">
              Tasdeeq
            </h3>
            <p className="text-sm text-gray-400 max-w-xs" data-id="element-1553">
              This section is reserved for future functionality and will be
              developed in a later phase.
            </p>
          </div>}
        {activeTab === 'calculated' && renderCalculated()}
        {activeTab === 'kyc' && renderKYC()}
        {activeTab === 'rules' && renderRules()}
        {activeTab === 'logs' && renderLogs()}
      </div>

      {/* Action Modal */}
      <Modal isOpen={showActionModal} onClose={() => setShowActionModal(false)} title={modalContent.title} data-id="element-1554">
        <div className="space-y-4" data-id="element-1555">
          <div className="flex items-start space-x-4" data-id="element-1556">
            <div className={`h-12 w-12 rounded-full ${modalContent.iconBg} flex items-center justify-center flex-shrink-0`} data-id="element-1557">
              {modalContent.icon}
            </div>
            <div className="flex-1" data-id="element-1558">
              <p className="text-sm text-gray-600" data-id="element-1559">
                {modalContent.description}
              </p>
              <div className="mt-2 text-sm text-gray-500" data-id="element-1560">
                <p data-id="element-1561">
                  <strong data-id="element-1562">Application:</strong> {app.id}
                </p>
                <p data-id="element-1563">
                  <strong data-id="element-1564">Merchant:</strong> {app.merchantName}
                </p>
                <p data-id="element-1565">
                  <strong data-id="element-1566">Amount:</strong> {formatCurrency(app.loanAmount)}
                </p>
              </div>
            </div>
          </div>

          {actionType === 'disburse' && <div data-id="element-1567">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1568">
                Disbursement Evidence <span className="text-red-500" data-id="element-1569">*</span>
              </label>
              <label htmlFor="disbursement-evidence" className="flex flex-col items-center justify-center w-full px-4 py-6 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-emerald-400 hover:bg-emerald-50/50 transition-colors" data-id="element-1570">
                <Upload className="h-6 w-6 text-gray-400 mb-2" data-id="element-1571" />
                <span className="text-sm font-medium text-gray-700" data-id="element-1572">
                  Click to upload attachments
                </span>
                <span className="text-xs text-gray-400 mt-1" data-id="element-1573">
                  Disbursement receipt, transfer proof, etc. (PDF, JPG, PNG)
                </span>
                <input id="disbursement-evidence" type="file" multiple accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={handleAttachmentChange} data-id="element-1574" />
              </label>
              {attachments.length > 0 && <ul className="mt-3 space-y-2" data-id="element-1575">
                  {attachments.map((file, index) => <li key={`${file.name}-${index}`} className="flex items-center justify-between rounded-md border border-gray-200 bg-gray-50 px-3 py-2" data-id="element-1576">
                      <div className="flex items-center space-x-2 min-w-0" data-id="element-1577">
                        <Paperclip className="h-4 w-4 text-gray-400 flex-shrink-0" data-id="element-1578" />
                        <span className="text-sm text-gray-700 truncate" data-id="element-1579">
                          {file.name}
                        </span>
                        <span className="text-xs text-gray-400 flex-shrink-0" data-id="element-1580">
                          {formatFileSize(file.size)}
                        </span>
                      </div>
                      <button type="button" onClick={() => removeAttachment(index)} className="p-1 text-gray-400 hover:text-red-500 transition-colors flex-shrink-0" aria-label={`Remove ${file.name}`} data-id="element-1581">
                        <X className="h-4 w-4" data-id="element-1582" />
                      </button>
                    </li>)}
                </ul>}
            </div>}

          <div data-id="element-1583">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1584">
              Comments{' '}
              {actionType === 'reject' && <span className="text-red-500" data-id="element-1585">*</span>}
            </label>
            <textarea value={comment} onChange={e => setComment(e.target.value)} rows={4} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder={actionType === 'reject' ? 'Please provide a reason for rejection...' : 'Add any comments or notes (optional)...'} data-id="element-1586" />
          </div>

          <div className="flex justify-end space-x-3 pt-4" data-id="element-1587">
            <Button variant="secondary" onClick={() => setShowActionModal(false)} data-id="element-1588">
              Cancel
            </Button>
            <Button variant={modalContent.buttonVariant} onClick={handleConfirmAction} isLoading={isProcessing} disabled={actionType === 'reject' && !comment.trim() || actionType === 'disburse' && attachments.length === 0} data-id="element-1589">
              {modalContent.buttonText}
            </Button>
          </div>
        </div>
      </Modal>
    </div>;
}